import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { testCaseAPI, csvAPI, datasetAPI } from '../services/api';

function CreateTestCase() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    project: '',
    description: '',
    endpoint: '',
    method: 'GET',
    auth: {
      type: 'none',
      token: '',
      key_name: 'X-API-Key',
      key_value: '',
      username: '',
      password: ''
    },
    success_codes: [200],
    timeout: 30
  });

  const [dataSource, setDataSource] = useState('upload'); // 'upload' or 'dataset'
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [mapping, setMapping] = useState({
    pathColumn: '',
    queryColumns: {},
    bodyColumns: {},
    headersColumns: {}
  });
  const [queryParams, setQueryParams] = useState([]);
  const [bodyParams, setBodyParams] = useState([]);
  const [headerParams, setHeaderParams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await datasetAPI.getAll();
      setDatasets(response.data);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAuthChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      auth: {
        ...prev.auth,
        [name]: value
      }
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);
      const response = await csvAPI.parse(file);
      setCsvData(response.data.data);
      setCsvColumns(response.data.columns);
      setStep(2);
    } catch (err) {
      setError('Failed to parse CSV: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDatasetSelect = async (e) => {
    const datasetId = e.target.value;
    if (!datasetId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await datasetAPI.getPreview(datasetId);
      setSelectedDataset(response.data);
      setCsvData(response.data.data);
      setCsvColumns(response.data.columns);
      setStep(2);
    } catch (err) {
      setError('Failed to load dataset: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { name: '', column: '' }]);
  };

  const addBodyParam = () => {
    setBodyParams([...bodyParams, { name: '', column: '' }]);
  };

  const addHeaderParam = () => {
    setHeaderParams([...headerParams, { name: '', column: '' }]);
  };

  const updateQueryParam = (index, field, value) => {
    const updated = [...queryParams];
    updated[index][field] = value;
    setQueryParams(updated);
  };

  const updateBodyParam = (index, field, value) => {
    const updated = [...bodyParams];
    updated[index][field] = value;
    setBodyParams(updated);
  };

  const updateHeaderParam = (index, field, value) => {
    const updated = [...headerParams];
    updated[index][field] = value;
    setHeaderParams(updated);
  };

  const removeQueryParam = (index) => {
    setQueryParams(queryParams.filter((_, i) => i !== index));
  };

  const removeBodyParam = (index) => {
    setBodyParams(bodyParams.filter((_, i) => i !== index));
  };

  const removeHeaderParam = (index) => {
    setHeaderParams(headerParams.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build mapping object
      const finalMapping = {
        pathColumn: mapping.pathColumn,
        queryColumns: {},
        bodyColumns: {},
        headersColumns: {}
      };

      queryParams.forEach(param => {
        if (param.name && param.column) {
          finalMapping.queryColumns[param.name] = param.column;
        }
      });

      bodyParams.forEach(param => {
        if (param.name && param.column) {
          finalMapping.bodyColumns[param.name] = param.column;
        }
      });

      headerParams.forEach(param => {
        if (param.name && param.column) {
          finalMapping.headersColumns[param.name] = param.column;
        }
      });

      let testCaseData;

      if (dataSource === 'dataset' && selectedDataset) {
        // Use dataset reference instead of storing all tests
        testCaseData = {
          ...formData,
          datasetId: selectedDataset._id,
          csvMapping: finalMapping,
          totalTests: selectedDataset.rowCount
        };
      } else {
        // Process CSV data with mapping (old way - for uploads)
        const processResponse = await csvAPI.process(csvData, finalMapping);
        const tests = processResponse.data.tests;

        testCaseData = {
          ...formData,
          tests,
          csvMapping: finalMapping,
          totalTests: tests.length
        };
      }

      const response = await testCaseAPI.create(testCaseData);
      navigate(`/test-case/${response.data._id}`);
    } catch (err) {
      setError('Failed to create test case: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <h2>Create New Test Case</h2>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {/* Step 1: Basic Configuration */}
        {step === 1 && (
          <div>
            <h3>Step 1: Basic Configuration</h3>

            <div className="form-group">
              <label>Test Case Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Project *</label>
              <input
                type="text"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                placeholder="e.g., walmart, amazon"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Endpoint Base URL *</label>
              <input
                type="text"
                name="endpoint"
                value={formData.endpoint}
                onChange={handleInputChange}
                placeholder="https://api.example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>HTTP Method</label>
              <select name="method" value={formData.method} onChange={handleInputChange}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>

            <div className="form-group">
              <label>Authentication Type</label>
              <select name="type" value={formData.auth.type} onChange={handleAuthChange}>
                <option value="none">None</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
                <option value="basic">Basic Auth</option>
              </select>
            </div>

            {formData.auth.type === 'bearer' && (
              <div className="form-group">
                <label>Bearer Token</label>
                <input
                  type="text"
                  name="token"
                  value={formData.auth.token}
                  onChange={handleAuthChange}
                />
              </div>
            )}

            {formData.auth.type === 'api_key' && (
              <>
                <div className="form-group">
                  <label>API Key Header Name</label>
                  <input
                    type="text"
                    name="key_name"
                    value={formData.auth.key_name}
                    onChange={handleAuthChange}
                  />
                </div>
                <div className="form-group">
                  <label>API Key Value</label>
                  <input
                    type="text"
                    name="key_value"
                    value={formData.auth.key_value}
                    onChange={handleAuthChange}
                  />
                </div>
              </>
            )}

            {formData.auth.type === 'basic' && (
              <>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.auth.username}
                    onChange={handleAuthChange}
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.auth.password}
                    onChange={handleAuthChange}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Success Status Codes (comma-separated)</label>
              <input
                type="text"
                value={formData.success_codes.join(', ')}
                onChange={(e) => {
                  const codes = e.target.value.split(',').map(c => parseInt(c.trim())).filter(c => !isNaN(c));
                  setFormData(prev => ({ ...prev, success_codes: codes }));
                }}
                placeholder="200, 201, 204"
              />
            </div>

            <div className="form-group">
              <label>Request Timeout (seconds)</label>
              <input
                type="number"
                name="timeout"
                value={formData.timeout}
                onChange={handleInputChange}
                min="1"
                max="300"
              />
            </div>

            <div className="form-group">
              <label>Data Source *</label>
              <select
                value={dataSource}
                onChange={(e) => setDataSource(e.target.value)}
              >
                <option value="upload">Upload CSV File</option>
                <option value="dataset">Use Existing Dataset</option>
              </select>
              <small>Choose to upload a new CSV or use an existing dataset</small>
            </div>

            {dataSource === 'upload' ? (
              <div className="form-group">
                <label>Upload CSV File *</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                {loading && <p>Parsing CSV...</p>}
              </div>
            ) : (
              <div className="form-group">
                <label>Select Dataset *</label>
                <select
                  onChange={handleDatasetSelect}
                  disabled={loading}
                  defaultValue=""
                >
                  <option value="">-- Select a dataset --</option>
                  {datasets.map(dataset => (
                    <option key={dataset._id} value={dataset._id}>
                      {dataset.name} ({dataset.rowCount.toLocaleString()} rows, {dataset.columns?.length} columns)
                    </option>
                  ))}
                </select>
                {loading && <p>Loading dataset...</p>}
                {datasets.length === 0 && (
                  <small style={{ color: '#ff6b6b' }}>
                    No datasets available. <a href="/datasets">Upload a dataset first</a>.
                  </small>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Field Mapping */}
        {step === 2 && (
          <div>
            <h3>Step 2: Map CSV Columns to Request Fields</h3>

            <div className="alert alert-info">
              CSV loaded with {csvData?.length || 0} rows and {csvColumns.length} columns
            </div>

            <div className="form-group">
              <label>Path Column (for URL path)</label>
              <select
                value={mapping.pathColumn}
                onChange={(e) => setMapping({ ...mapping, pathColumn: e.target.value })}
              >
                <option value="">-- None --</option>
                {csvColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <h4>Query Parameters</h4>
              {queryParams.map((param, index) => (
                <div key={index} className="flex mb-1">
                  <input
                    type="text"
                    placeholder="Parameter name"
                    value={param.name}
                    onChange={(e) => updateQueryParam(index, 'name', e.target.value)}
                  />
                  <select
                    value={param.column}
                    onChange={(e) => updateQueryParam(index, 'column', e.target.value)}
                  >
                    <option value="">-- Select Column --</option>
                    {csvColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeQueryParam(index)}
                    className="btn btn-danger"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button onClick={addQueryParam} className="btn btn-secondary">
                Add Query Parameter
              </button>
            </div>

            {(formData.method === 'POST' || formData.method === 'PUT' || formData.method === 'PATCH') && (
              <div className="form-group">
                <h4>Body Parameters</h4>
                {bodyParams.map((param, index) => (
                  <div key={index} className="flex mb-1">
                    <input
                      type="text"
                      placeholder="Parameter name"
                      value={param.name}
                      onChange={(e) => updateBodyParam(index, 'name', e.target.value)}
                    />
                    <select
                      value={param.column}
                      onChange={(e) => updateBodyParam(index, 'column', e.target.value)}
                    >
                      <option value="">-- Select Column --</option>
                      {csvColumns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeBodyParam(index)}
                      className="btn btn-danger"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button onClick={addBodyParam} className="btn btn-secondary">
                  Add Body Parameter
                </button>
              </div>
            )}

            <div className="form-group">
              <h4>Custom Headers</h4>
              {headerParams.map((param, index) => (
                <div key={index} className="flex mb-1">
                  <input
                    type="text"
                    placeholder="Header name"
                    value={param.name}
                    onChange={(e) => updateHeaderParam(index, 'name', e.target.value)}
                  />
                  <select
                    value={param.column}
                    onChange={(e) => updateHeaderParam(index, 'column', e.target.value)}
                  >
                    <option value="">-- Select Column --</option>
                    {csvColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeHeaderParam(index)}
                    className="btn btn-danger"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button onClick={addHeaderParam} className="btn btn-secondary">
                Add Header
              </button>
            </div>

            <div className="flex mt-3">
              <button onClick={() => setStep(1)} className="btn btn-secondary">
                Back
              </button>
              <button onClick={handleSubmit} className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Test Case'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateTestCase;
